import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link2, Sparkles, Loader2, Star, AlertTriangle, X } from "lucide-react";
import { apiError } from "@/lib/apiError";

const STEPS = ["Reading the product page...", "Finding images & price...", "Cleaning up details with AI..."];

const toForm = (res) => {
  const d = res.data;
  return {
    title: d.title || "", brand_name: d.brand || (res.blocked ? "" : res.site) || "", category: d.category || "",
    description: d.description || "", images: d.images || [], original_price: d.mrp || d.price || "",
    discounted_price: d.mrp && d.price && d.mrp > d.price ? d.price : "", rating: d.rating || "",
    review_count: d.review_count || "", reviews: d.reviews || [], highlights: d.highlights || [],
    affiliate_link: res.source_url, source_url: res.source_url, source_site: res.site,
    cashback_percent: "5", commission_percent: "5",
  };
};

export const Field = ({ label, children }) => <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;

export const ImagePicker = ({ images, onChange }) => (
  <div className="flex gap-2 flex-wrap" data-testid="import-images">
    {images.map((src, i) => (
      <div key={src} className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 ${i === 0 ? "border-primary-600" : "border-primary-100"}`}>
        <button type="button" onClick={() => onChange([src, ...images.filter((x) => x !== src)])} className="w-full h-full" title="Make main image"><img src={src} alt="" className="w-full h-full object-contain bg-white" /></button>
        <button type="button" onClick={() => onChange(images.filter((x) => x !== src))} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" data-testid={`import-image-remove-${i}`}><X className="w-3 h-3" /></button>
        {i === 0 && <span className="absolute bottom-0 inset-x-0 text-[9px] text-center bg-primary-600 text-white">MAIN</span>}
      </div>
    ))}
  </div>
);

export const LinkImporter = ({ categories, onSaved }) => {
  const { getAuthHeaders } = useAuth();
  const [url, setUrl] = useState("");
  const [step, setStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchLink = async () => {
    setStep(0);
    setResult(null);
    const t1 = setTimeout(() => setStep(1), 2500);
    const t2 = setTimeout(() => setStep(2), 5500);
    try {
      const res = await axios.post(`${API}/admin/import/preview`, { url }, { headers: getAuthHeaders(), timeout: 90000 });
      setResult(res.data);
      setForm(toForm(res.data));
      res.data.blocked || !res.data.success ? toast.warning(res.data.message) : toast.success(res.data.message);
    } catch (err) {
      toast.error(apiError(err, "Couldn't import this link"));
    }
    clearTimeout(t1);
    clearTimeout(t2);
    setStep(-1);
  };

  const save = async () => {
    const num = (v) => (v === "" || v === null ? null : parseFloat(v));
    if (!form.title || !num(form.original_price) || !form.images.length || !form.category) {
      toast.error("Title, price, category and at least one image are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/products/affiliate`, {
        brand_name: form.brand_name || form.source_site, title: form.title, description: form.description || form.title,
        image_url: form.images[0], images: form.images, affiliate_link: form.affiliate_link, category: form.category,
        original_price: num(form.original_price), discounted_price: num(form.discounted_price),
        commission_percent: num(form.commission_percent) || 0, cashback_percent: num(form.cashback_percent) || 0,
        rating: num(form.rating), review_count: form.review_count ? parseInt(form.review_count, 10) : null,
        reviews: form.reviews, highlights: form.highlights, source_url: form.source_url, source_site: form.source_site,
      }, { headers: getAuthHeaders() });
      toast.success("Product imported! Add other-store links from the compare button to enable price comparison.");
      setResult(null);
      setForm(null);
      setUrl("");
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Failed to save product"));
    }
    setSaving(false);
  };

  return (
    <Card className="border-2 border-primary-200 shadow-sm mb-6 overflow-hidden" data-testid="link-importer">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-secondary-600" /><h3 className="font-heading font-bold text-primary-900">Import product from any link</h3></div>
        <p className="text-sm text-primary-500">Paste an Amazon, Flipkart, Myntra or brand-site product link. We'll pull the images, price, description and reviews.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400" />
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.flipkart.com/..." className="pl-9 h-11 rounded-full" data-testid="import-url-input" />
          </div>
          <Button onClick={fetchLink} disabled={!url.trim() || step >= 0} className="h-11 rounded-full bg-primary-600 hover:bg-primary-700 px-6" data-testid="import-fetch-btn">
            {step >= 0 ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{step >= 0 ? "Fetching..." : "Fetch details"}
          </Button>
        </div>
        {step >= 0 && <p className="text-sm text-primary-600 animate-pulse" data-testid="import-step">{STEPS[step]}</p>}
        {result && form && (
          <div className="space-y-4 border-t pt-4" data-testid="import-preview">
            {(result.blocked || result.message !== "Imported successfully") && (
              <div className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="import-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{result.message}</div>
            )}
            <ImagePicker images={form.images} onChange={(v) => set("images", v)} />
            <Field label="Add image URL"><Input placeholder="Paste an image link and press Enter" onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value) { e.preventDefault(); set("images", [...form.images, e.currentTarget.value]); e.currentTarget.value = ""; } }} data-testid="import-add-image-input" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="import-title-input" /></Field>
              <Field label="Brand"><Input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="import-brand-input" /></Field>
              <Field label="MRP / Original price (₹)"><Input type="number" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} data-testid="import-mrp-input" /></Field>
              <Field label="Selling price (₹, optional)"><Input type="number" value={form.discounted_price} onChange={(e) => set("discounted_price", e.target.value)} data-testid="import-price-input" /></Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="import-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Affiliate link"><Input value={form.affiliate_link} onChange={(e) => set("affiliate_link", e.target.value)} data-testid="import-affiliate-input" /></Field>
              <Field label="Cashback %"><Input type="number" value={form.cashback_percent} onChange={(e) => set("cashback_percent", e.target.value)} data-testid="import-cashback-input" /></Field>
              <Field label="Commission %"><Input type="number" value={form.commission_percent} onChange={(e) => set("commission_percent", e.target.value)} data-testid="import-commission-input" /></Field>
            </div>
            <Field label="Description"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="import-description-input" /></Field>
            <div className="flex flex-wrap items-center gap-3 text-sm text-primary-700" data-testid="import-rating-summary">
              <Star className="w-4 h-4 fill-secondary-500 text-secondary-500" />
              <Input type="number" step="0.1" className="w-20 h-9" value={form.rating} onChange={(e) => set("rating", e.target.value)} placeholder="Rating" data-testid="import-rating-input" />
              <Input type="number" className="w-28 h-9" value={form.review_count} onChange={(e) => set("review_count", e.target.value)} placeholder="Ratings count" data-testid="import-review-count-input" />
              <span>{form.reviews.length} reviews imported</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setResult(null); setForm(null); }} data-testid="import-cancel-btn">Discard</Button>
              <Button onClick={save} disabled={saving} className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400 font-bold" data-testid="import-save-btn">{saving ? "Saving..." : "Save to M Ventures"}</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
